import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { Activity, CheckCircle2, XCircle, ArrowLeft, Radio } from "lucide-react";

const StatusPill = ({ ok, label }) => (
  <span className={"cad-chip text-[9px] uppercase inline-flex items-center gap-1 " +
                    (ok ? "cad-chip-green" : "cad-chip-red")}>
    {ok ? <CheckCircle2 size={10} /> : <XCircle size={10} />} {label}
  </span>
);

const isOk = (v) => v === "operational" || v === "up" || v === true;

export const OperationalReadiness = () => {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["public-status"],
    queryFn: async () => (await api.get("/cadastre360/trust/public-status")).data,
    refetchInterval: 30000,
    retry: false,
  });

  return (
    <div className="min-h-screen p-6 lg:p-12" data-testid="page-operational-readiness">
      <div className="flex items-center justify-between mb-8">
        <BrandMark size={140} />
        <Link to="/portal" className="cad-btn-outline px-3 py-1.5 rounded-sm text-[12px] inline-flex items-center gap-1.5">
          <ArrowLeft size={12} /> Public Portal
        </Link>
      </div>

      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#C99A2E] flex items-center gap-2">
            <Radio size={12} /> Cadastre360 · Public Operational Status
          </div>
          <h1 className="font-display text-4xl font-bold text-[#F6F8FA] mt-1">
            Sovereign Platform Availability
          </h1>
          <div className="text-[13px] text-[#B3C2CC] mt-2 max-w-3xl">
            Live public status of the Cadastre360 sovereign platform for the Ministry of Mines and Mining Development.
            Read-only, updated automatically.
          </div>
        </div>

        {isLoading && <div className="cad-panel p-8 text-[#B3C2CC]">Loading status…</div>}
        {isError && <div className="cad-panel p-8 text-[#E67E7E]">Status unavailable — please try again shortly.</div>}
        {data && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="public-status-grid">
            {[
              ["Platform", data.platform],
              ["Authentication", data.authentication],
              ["Certificate services", data.certificate_services],
              ["Timestamp services", data.timestamp_services],
              ["Verification portal", data.verification_portal],
              ["Certificate portal", data.public_certificate_portal],
              ["Redis / cache", data.redis],
            ].map(([label, v]) => (
              <div key={label} className="cad-panel p-4 flex items-center justify-between" data-testid={"status-row-" + label.toLowerCase().replace(/\s|\//g, "-")}>
                <div>
                  <div className="font-mono-cad text-[10px] uppercase tracking-[0.22em] text-[#7C93A5]">{label}</div>
                  <div className="text-[13px] text-[#F6F8FA] mt-0.5 font-mono-cad">{v}</div>
                </div>
                <StatusPill ok={isOk(v)} label={isOk(v) ? "operational" : v} />
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 flex flex-wrap gap-2">
          <Link to="/certificates" className="cad-btn-outline px-3 py-1.5 rounded-sm text-[12px] inline-flex items-center gap-1.5">
            <Activity size={12} /> Trust Services Portal
          </Link>
        </div>
        {data && (
          <div className="mt-4 text-[10px] font-mono-cad text-[#7C93A5] uppercase tracking-widest">
            Snapshot at {data.generated_at}
          </div>
        )}
      </div>
    </div>
  );
};
